import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';

// Recurrence editor UI — no JSON exposed.
// Value shape (matches backend expand_recurrence):
//   { freq: 'daily'|'weekly'|'monthly'|'yearly'|'custom',
//     interval: number, byweekday?: number[],
//     until?: 'YYYY-MM-DD', count?: number }
// `null` means non-recurring.

const WEEK_DAYS_EL = [
  { idx: 0, sh: 'Δε' }, { idx: 1, sh: 'Τρ' }, { idx: 2, sh: 'Τε' },
  { idx: 3, sh: 'Πε' }, { idx: 4, sh: 'Πα' }, { idx: 5, sh: 'Σα' }, { idx: 6, sh: 'Κυ' },
];
const WEEK_DAYS_EN = [
  { idx: 0, sh: 'Mo' }, { idx: 1, sh: 'Tu' }, { idx: 2, sh: 'We' },
  { idx: 3, sh: 'Th' }, { idx: 4, sh: 'Fr' }, { idx: 5, sh: 'Sa' }, { idx: 6, sh: 'Su' },
];

const FREQ_LABEL = {
  daily:   { el: 'Καθημερινά', en: 'Daily' },
  weekly:  { el: 'Εβδομαδιαία', en: 'Weekly' },
  monthly: { el: 'Μηνιαία', en: 'Monthly' },
  yearly:  { el: 'Ετήσια', en: 'Yearly' },
  custom:  { el: 'Προσαρμοσμένη', en: 'Custom' },
};

export default function RecurrenceEditor({ value, onChange, language = 'el' }) {
  const enabled = !!(value && value.freq);
  const weekDays = language === 'el' ? WEEK_DAYS_EL : WEEK_DAYS_EN;

  const set = (patch) => onChange({ ...(value || { freq: 'daily', interval: 1 }), ...patch });

  const toggleEnabled = (e) => {
    if (e.target.checked) onChange({ freq: 'daily', interval: 1 });
    else onChange(null);
  };

  const [endMode, setEndMode] = React.useState(() => {
    if (!value) return 'never';
    if (value.until) return 'on';
    if (value.count) return 'after';
    return 'never';
  });

  React.useEffect(() => {
    if (!value) return;
    if (endMode === 'never' && (value.until || value.count)) {
      set({ until: null, count: null });
    }
  }, [endMode]);

  const toggleWeekday = (idx) => {
    const cur = new Set(value?.byweekday || []);
    if (cur.has(idx)) cur.delete(idx); else cur.add(idx);
    set({ byweekday: Array.from(cur).sort((a, b) => a - b) });
  };

  return (
    <div className="border border-zinc-200 p-3 space-y-3" data-testid="recurrence-editor">
      <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={toggleEnabled}
          data-testid="rec-enable"
        />
        <span>{language === 'el' ? 'Επανάληψη' : 'Repeat this task'}</span>
      </label>

      {enabled && (
        <div className="space-y-3 pl-6">
          {/* Frequency + interval */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-zinc-500">
              {language === 'el' ? 'Κάθε' : 'Every'}
            </span>
            <Input
              type="number"
              min={1}
              max={365}
              value={value.interval || 1}
              onChange={(e) => set({ interval: Math.max(1, parseInt(e.target.value, 10) || 1) })}
              className="w-16 rounded-none"
              data-testid="rec-interval"
            />
            <select
              value={value.freq}
              onChange={(e) => set({ freq: e.target.value, byweekday: undefined })}
              className="border border-zinc-300 px-2 py-1.5 text-sm rounded-none bg-white"
              data-testid="rec-freq"
            >
              <option value="daily">{FREQ_LABEL.daily[language]}</option>
              <option value="weekly">{FREQ_LABEL.weekly[language]}</option>
              <option value="monthly">{FREQ_LABEL.monthly[language]}</option>
              <option value="yearly">{FREQ_LABEL.yearly[language]}</option>
              <option value="custom">{FREQ_LABEL.custom[language]}</option>
            </select>
          </div>

          {/* Weekly byweekday picker */}
          {value.freq === 'weekly' && (
            <div>
              <div className="text-xs text-zinc-500 mb-1">
                {language === 'el' ? 'Ημέρες της εβδομάδας' : 'Days of week'}
              </div>
              <div className="flex gap-1 flex-wrap">
                {weekDays.map(d => {
                  const active = (value.byweekday || []).includes(d.idx);
                  return (
                    <button
                      type="button"
                      key={d.idx}
                      onClick={() => toggleWeekday(d.idx)}
                      className={`px-2 py-1 text-xs border transition-colors ${active ? 'bg-emerald-600 text-white border-emerald-700' : 'border-zinc-300 hover:bg-zinc-50'}`}
                      data-testid={`rec-weekday-${d.idx}`}
                    >{d.sh}</button>
                  );
                })}
              </div>
            </div>
          )}

          {/* End condition */}
          <div>
            <div className="text-xs text-zinc-500 mb-1">
              {language === 'el' ? 'Λήξη επανάληψης' : 'Ends'}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  checked={endMode === 'never'}
                  onChange={() => setEndMode('never')}
                  data-testid="rec-end-never"
                />
                <span>{language === 'el' ? 'Ποτέ' : 'Never'}</span>
              </label>
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  checked={endMode === 'on'}
                  onChange={() => setEndMode('on')}
                  data-testid="rec-end-on"
                />
                <span>{language === 'el' ? 'Στις' : 'On'}</span>
                <Input
                  type="date"
                  value={value.until || ''}
                  disabled={endMode !== 'on'}
                  onChange={(e) => set({ until: e.target.value || null, count: null })}
                  className="w-40 rounded-none"
                  data-testid="rec-until"
                />
              </label>
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  checked={endMode === 'after'}
                  onChange={() => setEndMode('after')}
                  data-testid="rec-end-after"
                />
                <span>{language === 'el' ? 'Μετά από' : 'After'}</span>
                <Input
                  type="number"
                  min={1}
                  max={999}
                  value={value.count || ''}
                  disabled={endMode !== 'after'}
                  onChange={(e) => set({ count: Math.max(1, parseInt(e.target.value, 10) || 1), until: null })}
                  className="w-20 rounded-none"
                  data-testid="rec-count"
                />
                <span className="text-xs">{language === 'el' ? 'εμφανίσεις' : 'occurrences'}</span>
              </label>
            </div>
          </div>

          {/* Summary */}
          <div className="text-[11px] italic text-zinc-500" data-testid="rec-summary">
            {summarize(value, language)}
          </div>
        </div>
      )}
    </div>
  );
}

function summarize(v, lang) {
  if (!v) return '';
  const isEl = lang === 'el';
  const every = isEl ? 'Κάθε' : 'Every';
  const freqWord = ({
    daily:   isEl ? 'ημέρες' : 'day(s)',
    weekly:  isEl ? 'εβδομάδες' : 'week(s)',
    monthly: isEl ? 'μήνες' : 'month(s)',
    yearly:  isEl ? 'χρόνια' : 'year(s)',
    custom:  isEl ? 'ημέρες' : 'day(s)',
  })[v.freq];
  let parts = [`${every} ${v.interval || 1} ${freqWord}`];
  if (v.freq === 'weekly' && v.byweekday?.length) {
    const days = (isEl ? ['Δε','Τρ','Τε','Πε','Πα','Σα','Κυ'] : ['Mo','Tu','We','Th','Fr','Sa','Su']);
    parts.push((isEl ? 'στις' : 'on') + ' ' + v.byweekday.map(i => days[i]).join(', '));
  }
  if (v.until) parts.push((isEl ? 'έως' : 'until') + ` ${v.until}`);
  else if (v.count) parts.push((isEl ? 'για' : 'for') + ` ${v.count} ` + (isEl ? 'εμφανίσεις' : 'occurrences'));
  return parts.join(' · ');
}
